
import Vue, { PropType } from 'vue';
import IllinoisIcons from '../icons.vue';

type DonutSegmentInput = {
  value: number;
  // CSS variable name, e.g. '--primary', '--accent-500'
  colorVar: string;
};

type DonutSegmentComputed = {
  value: number;
  colorVar: string;
  length: number;
  offset: number;
};

export default Vue.extend({
  name: 'DonutRing',
  components: { IllinoisIcons },
  props: {
    segments: {
      type: Array as PropType<DonutSegmentInput[]>,
      required: true
    },
    size: {
      type: Number,
      default: 120
    },
    strokeWidth: {
      type: Number,
      default: 12
    },
    rounded: {
      type: Boolean,
      default: false
    },
    startAngle: {
      // Degrees. -90 starts from the top (12 o'clock)
      type: Number,
      default: -90
    },
    centerIcon: {
      type: String,
      required: false
    },
    iconSize: {
      type: Number,
      default: 20
    }
  },
  computed: {
    half(): number {
      return this.size / 2;
    },
    radius(): number {
      return this.half - this.strokeWidth / 2;
    },
    circumference(): number {
      return 2 * Math.PI * this.radius;
    },
    totalValue(): number {
      return this.segments
        .map(s => (typeof s.value === 'number' && s.value > 0 ? s.value : 0))
        .reduce((a, b) => a + b, 0);
    },
    rotationTransform(): string {
      return `rotate(${this.startAngle} ${this.half} ${this.half})`;
    },
    normalizedSegments(): DonutSegmentComputed[] {
      const total = this.totalValue;
      let segmentsForCalc: { value: number; colorVar: string }[] = [];

      // Fallback 50/50 quando i segmenti sono due e total è 0
      if (!total || total <= 0) {
        if (this.segments.length === 2) {
          segmentsForCalc = this.segments.map(s => ({
            value: 1,
            colorVar: s.colorVar
          }));
        } else {
          return [];
        }
      } else {
        segmentsForCalc = this.segments
          .map(s => ({
            value: typeof s.value === 'number' && s.value > 0 ? s.value : 0,
            colorVar: s.colorVar
          }))
          .filter(s => s.value > 0);
      }

      const sum = segmentsForCalc.reduce((acc, s) => acc + s.value, 0);
      if (sum <= 0) return [];

      let cumulativeRatio = 0;
      const circ = this.circumference;

      return segmentsForCalc.map(s => {
        const ratio = s.value / sum;
        const length = ratio * circ;
        const offset = circ * (1 - cumulativeRatio);
        cumulativeRatio += ratio;
        return {
          value: s.value,
          colorVar: s.colorVar,
          length,
          offset
        };
      });
    }
  }
});
